import * as d3 from "d3";
import appendEventFunctions from "./Auxiliar/appendEventFunctions";

export default class HighlightingManager {
  // Public:
  constructor(cParams) {
    this.saveParams(cParams);
    this.initTimeoutsArray();
  }

  compute() {
    this.createDelayedHighlightingFunction();
    this.createToggleHighlightingFunction();
    this.attachFunctions();
    this.abortAll();
    this.enableHighlighting();
  }

  abortAll() {
    this.cancelAllTimeouts();
    this.restore();
  }

  highlightNode(targetNode) {
    this.cancelAllTimeouts();
    if (targetNode !== undefined) {
      this.highlight(targetNode);
    } else {
      this.restore();
    }
  }

  // Private:
  saveParams(cParams) {
    this._elementId = cParams.elementId;
    this._nodeParams = cParams.nodesSettings;
    this._linkParams = cParams.linksSettings;
    this._textParams = cParams.textSettings;
    this._transitionTime = cParams.highlightingSettings.transitionTime;
    this._triggerTime = cParams.highlightingSettings.triggerTime;
  }

  initTimeoutsArray() {
    this._timeouts = [];
  }

  createDelayedHighlightingFunction() {
    const initTimeout = (circle) => this.initTimeout(circle);
    const abortAll = () => this.abortAll();
    const isThereActiveTimeouts = () => this.isThereActiveTimeouts();
    const func = (event) => {
      const target = event.target;
      const localName = target.localName;
      switch (localName) {
        case "circle":
        case "text":
          const thereAreNoActiveTimeouts = !isThereActiveTimeouts();
          if (thereAreNoActiveTimeouts) initTimeout(target);
          break;
        default:
          abortAll();
          break;
      }
    };
    this._delayedHighlightingFunc = func;
  }

  createToggleHighlightingFunction() {
    let func = (event) => {
      const target = event.target;
      let targetNode = target.__data__;
      this.highlightNode(targetNode);
    };
    this._toggledHighlightingFunc = func;
  }

  attachFunctions() {
    const elementId = this._elementId;
    let chart = document.getElementById(elementId);
    chart.onclick = appendEventFunctions([
      chart.onclick,
      this._toggledHighlightingFunc,
    ]);
    chart.enableHighlighting = () => this.enableHighlighting();
    chart.disableHighlighting = () => this.disableHighlighting();
  }

  initTimeout(circle) {
    const elementId = this._elementId;
    const element = document.getElementById(elementId);
    const triggerTime = this._triggerTime;
    this.abortAll();
    const highlightingIsEnabled = this.isHighlightingEnabled();
    if (highlightingIsEnabled) {
      let targetNode = circle.__data__;
      let func = () => {
        this.highlight(targetNode);
        this.cancelAllTimeouts();
      };
      let timeout = setTimeout(func, triggerTime);
      this.addTimeout(timeout);
      element.addTimeout(timeout);
    }
  }

  cancelAllTimeouts() {
    const elementId = this._elementId;
    const element = document.getElementById(elementId);
    let timeouts = this._timeouts;
    timeouts.forEach((timeout) => {
      clearTimeout(timeout);
      element.deleteTimeout(timeout);
    });
    this.resetTimeouts();
  }

  highlight(targetNode) {
    if (targetNode !== undefined) {
      this.highlightNodes(targetNode);
      this.highlightLinks(targetNode);
      this.highlightLabels(targetNode);
    }
  }

  restore() {
    this.restoreNodes();
    this.restoreLinks();
    this.restoreLabels();
  }

  highlightNodes(targetNode) {
    const lowValue = this._nodeParams.lowValue;
    const transitionTime = this._transitionTime;
    let numbers = this.findRelatedNodesNumbers(targetNode);
    let normalOpacityFunc = this.createNodeNormalOpacityFunction();
    let lowOpacityFunc = () => lowValue;
    let opacityFunc = this.createHighlightedOpacityFunction(
      numbers,
      normalOpacityFunc,
      lowOpacityFunc
    );
    const elementId = this._elementId;
    d3.select("#" + elementId)
      .select("svg")
      .selectAll("circle")
      .transition()
      .duration(transitionTime)
      .style("opacity", opacityFunc);
  }

  highlightLinks(targetNode) {
    let highValue = this._linkParams.highValue;
    let lowValue = this._linkParams.lowValue;
    let normalOpacityFunc = () => highValue;
    let lowOpacityFunc = () => lowValue;
    let transitionTime = this._transitionTime;
    let numbers = targetNode.linksNumbers;
    let opacityFunc = this.createHighlightedOpacityFunction(
      numbers,
      normalOpacityFunc,
      lowOpacityFunc
    );
    const elementId = this._elementId;
    d3.select("#" + elementId)
      .select("svg")
      .selectAll("line")
      .transition()
      .duration(transitionTime)
      .style("opacity", opacityFunc);
  }

  highlightLabels(targetNode) {
    const basicOpacityFunc = this.getLabelsOpacity();
    let lowValue = Math.min(this._textParams.lowValue, basicOpacityFunc());
    let lowOpacityFunc = () => lowValue;
    let transitionTime = this._transitionTime;
    let numbers = this.findRelatedNodesNumbers(targetNode);
    let opacityFunc = this.createHighlightedOpacityFunction(
      numbers,
      basicOpacityFunc,
      lowOpacityFunc
    );
    const elementId = this._elementId;
    d3.select("#" + elementId)
      .select("svg")
      .selectAll("text")
      .transition()
      .duration(transitionTime)
      .style("opacity", opacityFunc);
    this.setGlobalLabelOpacityFunction(opacityFunc);
  }

  restoreNodes() {
    const opacityFunc = this.createNodeNormalOpacityFunction();
    let transitionTime = this._transitionTime;
    const elementId = this._elementId;
    d3.select("#" + elementId)
      .select("svg")
      .selectAll("circle")
      .transition()
      .duration(transitionTime)
      .style("opacity", opacityFunc);
  }

  restoreLinks() {
    let highValue = this._linkParams.highValue;
    let transitionTime = this._transitionTime;
    const elementId = this._elementId;
    d3.select("#" + elementId)
      .select("svg")
      .selectAll("line")
      .transition()
      .duration(transitionTime)
      .style("opacity", highValue);
  }

  restoreLabels() {
    let highValue = this.getLabelsOpacity();
    let transitionTime = this._transitionTime;
    const elementId = this._elementId;
    d3.select("#" + elementId)
      .select("svg")
      .selectAll("text")
      .transition()
      .duration(transitionTime)
      .style("opacity", highValue);
    this.setGlobalLabelOpacityFunction(highValue);
  }

  enableHighlighting() {
    this._highlightingIsEnabled = true;
  }

  disableHighlighting() {
    this._highlightingIsEnabled = false;
    this.abortAll();
  }

  isHighlightingEnabled() {
    return this._highlightingIsEnabled;
  }

  findRelatedNodesNumbers(node) {
    let type = node.type;
    let indices = [];
    switch (type) {
      case "master":
        let masterIndex = node.number;
        let slavesIndices = node.slaveNumbers;
        indices.selfConcat(masterIndex);
        indices.selfConcat(slavesIndices);
        break;
      case "slave":
        let slaveIndex = node.number;
        let masterIndices = [];
        node.ownersNodes.forEach((node) => {
          let index = node.number;
          masterIndices.push(index);
        });
        indices.selfConcat(slaveIndex);
        indices.selfConcat(masterIndices);
        break;
      default:
        console.error("Invalid node type.");
        break;
    }
    return indices;
  }

  createHighlightedOpacityFunction(numbers, normalOpacityFunc, lowOpacityFunc) {
    let shouldBeHighlightedFunc = (n) =>
      this.doesNumberBelongToArray(numbers, n);
    let func = function(d) {
      let number = d.number;
      if (shouldBeHighlightedFunc(number)) return normalOpacityFunc(d);
      else return lowOpacityFunc(d);
    };
    return func;
  }

  createNodeNormalOpacityFunction() {
    const highValue = this._nodeParams.highValue;
    let func = function(d) {
      if (typeof d === "undefined") {
        throw new Error("Expected <node>, found undefined");
      } else {
        const type = d.type;
        switch (type) {
          case "master":
            return 1.0;
          case "slave":
            return highValue;
          default:
            throw new Error("Invalid node type: " + type);
        }
      }
    };
    return func;
  }

  doesNumberBelongToArray(array, number) {
    if (array && typeof number === "number" && array.indexOf(number) !== -1)
      return true;
    else return false;
  }

  addTimeout(timeout) {
    this._timeouts.push(timeout);
  }

  isThereActiveTimeouts() {
    const thereIsTimeOuts = this._timeouts.length > 0;
    if (thereIsTimeOuts) return true;
    else return false;
  }

  resetTimeouts() {
    this._timeouts = [];
  }

  getLabelsOpacity() {
    const elementId = this._elementId;
    const element = document.getElementById(elementId);
    const labels = d3
      .select("#" + elementId)
      .select("svg")
      .selectAll("text")._groups[0];
    const thereIsLabels = labels.length > 0;
    if (thereIsLabels) {
      return element.labelsOpacityFunction;
    } else return 0;
  }

  setGlobalLabelOpacityFunction(func) {
    const elementId = this._elementId;
    const element = document.getElementById(elementId);
    element.labelsHighlightingOpacityFunction = func;
  }
}
